import createMiddleware from '../helper';
import * as mapActions from '../../actions/map.actions';
import * as infoboxActions from '../../actions/infobox.actions';
import * as formFlowActions from '../../actions/formFlow.actions';
import * as patientActions from '../../actions/patient.actions';
import * as requestsActions from '../../actions/requests.actions';
import * as locationActions from '../../actions/location.actions';
import * as chiefComplaintActions from '../../actions/chiefComplaint.actions';
import { chiefComplaintNotAllowed, disabledByPcrStatus } from '../../../flags/prebilling.flag';
import { trackEvent } from '../../../services/datadog';

const unsetBillerVerifiedBeforeNextAction =
  ({ dispatch, getState }) =>
  next =>
  (action = {}) => {
    const types = [
      mapActions.setMapToWaypoint.match(action),
      mapActions.setMapFromWaypoint.match(action),
      mapActions.setMapPickupLocationByAgency.match(action),
      mapActions.setMapDestinationLocationByAgency.match(action),
    ];

    const { user, formFlow, requests } = getState();
    const isAdmin = user.isUserAmbAdmin;

    if (!formFlow.loadRequestInProgress && action && types.some(Boolean)) {
      const isSelectedNonFacilityLocation = !!action.payload.typeof && action.payload.typeof !== 'facility';
      if (requests.isBillerVerified && isSelectedNonFacilityLocation && isAdmin) {
        trackEvent('BillerVerified: Unchecking due map location change', {
          requestId: requests.request?.id,
          currentBillerVerified: requests.isBillerVerified,
          address: action.payload?.address,
        });
        dispatch(infoboxActions.billerVerifiedChange(false));
      }
    }

    next(action);
  };

const unsetBillerVerifiedOnPayerChangeMdl =
  ({ dispatch, getState }) =>
  next =>
  action => {
    const { user, requests } = getState();
    const isAdmin = user.isUserAmbAdmin;

    if (infoboxActions.setPayerInfo.match(action) && isAdmin && requests.isBillerVerified) {
      trackEvent('BillerVerified: Unchecking due to setPayerInfo', {
        requestId: requests.request?.id,
        currentBillerVerified: requests.isBillerVerified,
        payerCoordinationTypeId: action.payload?.coordinationTypeId,
      });
      dispatch(infoboxActions.billerVerifiedChange(false));
    }

    next(action);
  };

const unsetBillerVerifiedAfterNextAction =
  ({ dispatch, getState }) =>
  next =>
  (action = {}) => {
    next(action);

    const types = [
      mapActions.setResidence.match(action),
      mapActions.clearPickupPlace.match(action),
      mapActions.clearDestinationPlace.match(action),
      chiefComplaintActions.setChiefComplaint.match(action),
      patientActions.setPatientInfo.match(action),
    ];

    const chiefComplaintSetToOther = state =>
      chiefComplaintActions.setChiefComplaint.match(action) && chiefComplaintNotAllowed(state);

    const patientInfo =
      patientActions.setPatientInfo.match(action) &&
      getState().agency.selectedAgency &&
      getState().agency.selectedAgency.patientAddressRequired &&
      !Object.getOwnPropertyDescriptor(action.payload, 'address2');

    const { formFlow, requests, user } = getState();
    const isAdmin = user.isUserAmbAdmin;

    if (!formFlow.loadRequestInProgress && action && types.some(Boolean)) {
      if (
        requests.isBillerVerified &&
        !disabledByPcrStatus(getState()) &&
        isAdmin &&
        (patientInfo || chiefComplaintSetToOther(getState()))
      ) {
        const reason = patientInfo
          ? 'setPatientInfo with missing address2 when selectedAgency requires patient address'
          : 'chief complaint set to other';
        trackEvent(`BillerVerified: Unchecking due to ${reason}`, {
          requestId: requests.request?.id,
          currentBillerVerified: requests.isBillerVerified,
        });
        dispatch(infoboxActions.billerVerifiedChange(false));
      }
    }

    if (mapActions.setResidence.match(action) && requests.request.id && !formFlow.loadRequestInProgress) {
      dispatch(mapActions.asyncUpdateResidence());
      dispatch(formFlowActions.autoSaveInProgress(true));
    }
  };

const updateIsBillerVerified = createMiddleware(
  infoboxActions.billerVerifiedChange,
  ({ payload, getState, dispatch }) => {
    const state = getState();
    if (state.requests.request.id && !state.formFlow.loadRequestInProgress) {
      trackEvent('BillerVerified: Change dispatched', {
        newValue: payload,
        requestId: state.requests.request?.id,
        coveragesDirty: state.infobox.coveragesDirty,
        userType: state.user.model?.userType,
      });
      // updates the UI
      dispatch(
        requestsActions.setIsBillerVerified({
          isBillerVerified: payload,
          billerVerifiedBy: state.user.model.fullName,
        }),
      );
      const previousBillerVerified = state.requests.isBillerVerified;
      const previousBillerVerifiedBy = state.requests.billerVerifiedBy;
      // makes an API call to update the value in the backend
      dispatch(
        infoboxActions.asyncAutoSaveBillerVerified({
          isBillerVerified: payload,
          previousBillerVerified,
          previousBillerVerifiedBy,
        }),
      );
      dispatch(locationActions.clearSearchResults());
      dispatch(formFlowActions.autoSaveInProgress(true));
    }
  },
);

const middlewares = [
  unsetBillerVerifiedBeforeNextAction,
  unsetBillerVerifiedAfterNextAction,
  updateIsBillerVerified,
  unsetBillerVerifiedOnPayerChangeMdl,
];

export default middlewares;
